/**
 * The `/content` header (SPEC `Content Performance` § Navigation & chrome).
 *
 * `ViewHeader`'s two variants, verbatim: the sticky translucent **pill** in Metrics mode, and the
 * full-width **flat bar** in Posts/Comments mode, where the grid runs edge-to-edge and the bar has
 * room for search and the Columns picker. The mode switcher is plain text, absolutely centered, so
 * it never shifts as the right-side controls change between modes.
 *
 * Two things are deliberate here:
 *
 *  - **The granularity trigger names what is on screen, not what was asked for.** §8.5's degrade
 *    walk can answer a Daily request with Months; the trigger therefore shows `meta.granularity`
 *    once it has arrived, while the menu's radio still marks the *selected* value — so the label
 *    never claims a resolution the chart does not have, and clicking still means what it says.
 *  - **The Columns picker and the Export CSV link live in the same per-mode block**, because the
 *    CSV exports the *visible* columns and both need one layout. The layout hook is cache-backed,
 *    so this instance and the table's are the same layout (see `useContentColumnLayout`), and it is
 *    only mounted in the mode that owns it — Metrics never reads a `columns:content-*` blob.
 *
 * The quiet sub-row is `meta.summaryDisplay`, rendered verbatim (§12.5). Posts/Comments are
 * fullscreen and carry their facts in the grid footer instead, exactly as `/view`'s Posts mode does.
 */

import { Link } from 'react-router';
import {
  BUCKET_GRANULARITIES,
  CONTENT_MODES,
  getContentCommentsColumn,
  getContentPostsColumn,
  type BucketGranularity,
  type ContentColumnDef,
  type ContentCommentsQuery,
  type ContentMode,
  type ContentPostsQuery,
  type ViewRangeState,
} from '@lw/shared';
import { contentExportUrl } from '../../api/content';
import { HOME_PATH } from '../../app/routes';
import {
  CalendarIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ColumnsIcon,
  DownloadIcon,
  Menu,
  MenuItem,
  MenuSeparator,
  SearchIcon,
  UploadIcon,
  cx,
} from '../../components';
import { formatDateCompact } from '../../lib/datetime';
import { RangePanel } from '../view/RangePanel';
import { RANGE_PRESET_LABEL, type ResolvedRange } from '../view/viewState';
import {
  CONTENT_GRANULARITY_LABEL,
  CONTENT_MODE_LABEL,
  type ContentState,
} from './contentState';
import {
  CONTENT_COMMENTS_LAYOUT,
  CONTENT_POSTS_LAYOUT,
  useContentColumnLayout,
  type ContentColumnLayoutHandle,
} from './useContentColumnLayout';

/** Action-button glyph size — the view header's, since this is the same pill. */
const ACTION_ICON = 13;

/* ------------------------------------------------------------ shared actions */

function RangeAction({
  range,
  resolvedRange,
  onRangeChange,
  timezone,
  now,
}: {
  range: ViewRangeState;
  resolvedRange: ResolvedRange;
  onRangeChange: (next: ViewRangeState) => void;
  timezone: string;
  now: number;
}) {
  const label =
    range.preset === 'custom'
      ? `${formatDateCompact(resolvedRange.from, timezone)} – ${formatDateCompact(resolvedRange.to, timezone)}`
      : RANGE_PRESET_LABEL[range.preset];

  return (
    <Menu
      label={
        <>
          <CalendarIcon size={ACTION_ICON} />
          <span className="table-action__label">{label}</span>
        </>
      }
      align="right"
      panelClassName="menu__panel--pad"
    >
      {() => (
        <RangePanel
          range={range}
          resolved={resolvedRange}
          onChange={onRangeChange}
          timezone={timezone}
          now={now}
        />
      )}
    </Menu>
  );
}

function ImportAction({ onImport }: { onImport: () => void }) {
  return (
    <button
      type="button"
      className="table-action table-action--icon"
      aria-label="Import an analytics export"
      title="Import an analytics export"
      onClick={onImport}
    >
      <UploadIcon size={ACTION_ICON} />
    </button>
  );
}

/** The whole merged dataset in its native shapes — the one export every mode offers (#93). */
const FULL_JSON_LABEL = 'JSON — full merged dataset';

function ExportMenu({ csvHref }: { csvHref?: string }) {
  return (
    <Menu
      label={<DownloadIcon size={ACTION_ICON} />}
      ariaLabel="Export"
      align="right"
      triggerClassName="table-action table-action--icon"
    >
      {(close) => (
        <>
          {csvHref === undefined ? null : (
            <a className="menu__item" href={csvHref} onClick={close} download>
              CSV — visible columns
            </a>
          )}
          <a
            className="menu__item"
            href={contentExportUrl({ format: 'json' })}
            onClick={close}
            download
          >
            {FULL_JSON_LABEL}
          </a>
        </>
      )}
    </Menu>
  );
}

function GranularityAction({
  selected,
  effective,
  onChange,
}: {
  selected: BucketGranularity;
  /** `meta.granularity` — what the chart actually uses; absent until the first response. */
  effective: BucketGranularity | undefined;
  onChange: (next: BucketGranularity) => void;
}) {
  return (
    <Menu
      label={
        <>
          <span className="table-action__label">
            {CONTENT_GRANULARITY_LABEL[effective ?? selected]}
          </span>
          <ChevronDownIcon size={11} />
        </>
      }
      ariaLabel="Granularity"
      align="right"
    >
      {(close) => (
        <>
          {BUCKET_GRANULARITIES.map((granularity) => (
            <MenuItem
              key={granularity}
              role="menuitemradio"
              checked={granularity === selected}
              onClick={() => {
                onChange(granularity);
                close();
              }}
            >
              {CONTENT_GRANULARITY_LABEL[granularity]}
            </MenuItem>
          ))}
        </>
      )}
    </Menu>
  );
}

function ColumnsAction<Id extends string>({
  layout,
  getColumn,
}: {
  layout: ContentColumnLayoutHandle<Id>;
  getColumn: (id: Id) => ContentColumnDef;
}) {
  return (
    <Menu
      label={<ColumnsIcon size={ACTION_ICON} />}
      ariaLabel="Columns"
      align="right"
      triggerClassName="table-action table-action--icon"
      panelClassName="menu__panel--wide"
    >
      {() => (
        <>
          <div className="menu__checklist">
            {layout.order.map((columnId) => (
              <label className="checklist__item" key={columnId}>
                <input
                  type="checkbox"
                  checked={!layout.hidden.has(columnId)}
                  onChange={() => layout.toggle(columnId)}
                />
                {/* The grid header abbreviates ("I ×"); the picker spells it out, the
                    `viewState.sortKeyLabel` precedent. */}
                <span className="checklist__label">
                  {getColumn(columnId).exportLabel ?? getColumn(columnId).label}
                </span>
              </label>
            ))}
          </div>
          <MenuSeparator />
          <MenuItem onClick={layout.reset}>Reset to defaults</MenuItem>
          <MenuItem disabled>
            {layout.pinned === null
              ? 'Drag a header to reorder'
              : `Drag a header to reorder · ${getColumn(layout.pinned).label} stays first`}
          </MenuItem>
        </>
      )}
    </Menu>
  );
}

function SearchBox({
  search,
  onSearchChange,
  placeholder,
  label,
}: {
  search: string;
  onSearchChange: (next: string) => void;
  placeholder: string;
  label: string;
}) {
  return (
    <div className="view-search">
      <SearchIcon size={14} />
      <input
        className="view-search__input"
        type="search"
        placeholder={placeholder}
        aria-label={label}
        value={search}
        onChange={(event) => onSearchChange(event.target.value)}
      />
    </div>
  );
}

/* ------------------------------------------------------- per-mode action rows */

type CommonActionProps = {
  range: ViewRangeState;
  resolvedRange: ResolvedRange;
  onRangeChange: (next: ViewRangeState) => void;
  timezone: string;
  now: number;
  onImport: () => void;
};

type TableActionProps = CommonActionProps & {
  search: string;
  onSearchChange: (next: string) => void;
};

/** Search · Columns · Range · Import · Export — the fixed Posts-mode sequence. */
function PostsActions({
  params,
  search,
  onSearchChange,
  ...common
}: TableActionProps & { params: Partial<ContentPostsQuery> }) {
  const layout = useContentColumnLayout(CONTENT_POSTS_LAYOUT);
  const visible = layout.order.filter((columnId) => !layout.hidden.has(columnId));

  return (
    <>
      <SearchBox
        search={search}
        onSearchChange={onSearchChange}
        placeholder="Search post text or link…"
        label="Search imported posts"
      />
      <ColumnsAction layout={layout} getColumn={getContentPostsColumn} />
      <RangeAction {...common} />
      <ImportAction onImport={common.onImport} />
      <ExportMenu
        csvHref={contentExportUrl({ ...params, format: 'csv', table: 'posts', columns: visible })}
      />
    </>
  );
}

function CommentsActions({
  params,
  search,
  onSearchChange,
  ...common
}: TableActionProps & { params: Partial<ContentCommentsQuery> }) {
  const layout = useContentColumnLayout(CONTENT_COMMENTS_LAYOUT);
  const visible = layout.order.filter((columnId) => !layout.hidden.has(columnId));

  return (
    <>
      <SearchBox
        search={search}
        onSearchChange={onSearchChange}
        placeholder="Search comment text or author…"
        label="Search imported comments"
      />
      <ColumnsAction layout={layout} getColumn={getContentCommentsColumn} />
      <RangeAction {...common} />
      <ImportAction onImport={common.onImport} />
      <ExportMenu
        csvHref={contentExportUrl({
          ...params,
          format: 'csv',
          table: 'comments',
          columns: visible,
        })}
      />
    </>
  );
}

/* --------------------------------------------------------------------- header */

export type ContentHeaderProps = {
  mode: ContentMode;
  onModeChange: (mode: ContentMode) => void;
  state: ContentState;
  resolvedRange: ResolvedRange;
  onRangeChange: (next: ViewRangeState) => void;
  onGranularityChange: (next: BucketGranularity) => void;
  /** `meta.granularity` from #87 — the granularity actually in use. */
  effectiveGranularity: BucketGranularity | undefined;
  /** `meta.summaryDisplay` from #87; blank while the first response is in flight. */
  summaryDisplay: string | undefined;
  search: string;
  onSearchChange: (next: string) => void;
  timezone: string;
  now: number;
  onImport: () => void;
  postsParams: Partial<ContentPostsQuery>;
  commentsParams: Partial<ContentCommentsQuery>;
};

export function ContentHeader({
  mode,
  onModeChange,
  state,
  resolvedRange,
  onRangeChange,
  onGranularityChange,
  effectiveGranularity,
  summaryDisplay,
  search,
  onSearchChange,
  timezone,
  now,
  onImport,
  postsParams,
  commentsParams,
}: ContentHeaderProps) {
  const isTable = mode !== 'metrics';
  const common: CommonActionProps = {
    range: state.range,
    resolvedRange,
    onRangeChange,
    timezone,
    now,
    onImport,
  };

  return (
    <>
      <div className={cx('view-header', isTable && 'view-header--flat')}>
        <div className={isTable ? 'view-header__bar' : 'view-header__pill'}>
          <div className="view-header__left">
            <Link className="icon-btn" to={HOME_PATH} aria-label="Back to profiles">
              <ChevronLeftIcon />
            </Link>
            <span className="view-header__name">Content Performance</span>
          </div>

          <nav className="view-header__modes" aria-label="Content mode">
            {CONTENT_MODES.map((candidate) => (
              <button
                key={candidate}
                type="button"
                className={cx('mode-tab', candidate === mode && 'is-active')}
                aria-current={candidate === mode ? 'page' : undefined}
                onClick={() => onModeChange(candidate)}
              >
                {CONTENT_MODE_LABEL[candidate]}
              </button>
            ))}
          </nav>

          <div className="view-header__actions">
            {mode === 'metrics' ? (
              <>
                <GranularityAction
                  selected={state.granularity}
                  effective={effectiveGranularity}
                  onChange={onGranularityChange}
                />
                <RangeAction {...common} />
                <ImportAction onImport={onImport} />
                <ExportMenu />
              </>
            ) : null}

            {mode === 'posts' ? (
              <PostsActions
                {...common}
                params={postsParams}
                search={search}
                onSearchChange={onSearchChange}
              />
            ) : null}

            {mode === 'comments' ? (
              <CommentsActions
                {...common}
                params={commentsParams}
                search={search}
                onSearchChange={onSearchChange}
              />
            ) : null}
          </div>
        </div>
      </div>

      {/* Fullscreen table modes carry these facts in the grid footer instead. */}
      {isTable ? null : <div className="view-sub">{summaryDisplay ?? ' '}</div>}
    </>
  );
}

export default ContentHeader;
